import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { toOptions } from '../../warehouse/warehouse.utils';
import { syncDeadlineTime, TaskFormGroup } from '../tasks.form';
import { ResolvedTaskLink, TASK_PRIORITIES, TaskUser } from '../tasks.types';
import { TASK_TITLE_MAX_LENGTH } from '../tasks.utils';

let nextFieldsId = 0;

/**
 * The fields a task has, for the form of a new task and for the task card: title, description, executor, observers,
 * deadline, priority, the review flag and the links. The owner keeps the form and what it does with it.
 */
@Component({
  selector: 'app-task-fields',
  templateUrl: './task-fields.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
})
export class TaskFieldsComponent implements OnInit, OnChanges {
  @Input({ required: true }) form!: TaskFormGroup;
  @Input() users: TaskUser[] = [];
  /** Cards, contacts and companies belong to CRM 2.0: without it a task has nothing to be linked to */
  @Input() canLink = true;
  /** The links open what they point to: the form of a new task has nothing to open yet */
  @Input() openableLinks = false;
  @Input() canOpenCards = true;
  @Output() openLink = new EventEmitter<ResolvedTaskLink>();

  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly uid = nextFieldsId++;
  protected readonly titleMaxLength = TASK_TITLE_MAX_LENGTH;
  protected readonly priorityOptions = toOptions(TASK_PRIORITIES, 'crm_tasks.priority.');
  /** Picks one more observer: it is cleared as soon as the observer is added to the list */
  protected readonly observerPicker = new FormControl<string | null>(null);

  protected assigneeOptions: TaskUser[] = [];
  protected observerOptions: TaskUser[] = [];
  protected observers: TaskUser[] = [];

  ngOnInit(): void {
    const { deadline_date, assignee_id, observer_ids } = this.form.controls;
    deadline_date.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => syncDeadlineTime(this.form));
    merge(assignee_id.valueChanges, observer_ids.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshPeople());
    this.observerPicker.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (!id) return;

      this.observerPicker.reset(null, { emitEvent: false });
      this.setObservers([...observer_ids.value.filter((observerId) => observerId !== id), id]);
    });
    this.refreshPeople();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) this.refreshPeople();
  }

  protected removeObserver(id: string): void {
    this.setObservers(this.form.controls.observer_ids.value.filter((observerId) => observerId !== id));
  }

  private setObservers(ids: string[]): void {
    const control = this.form.controls.observer_ids;
    control.setValue(ids);
    control.markAsDirty();
  }

  /**
   * Who the pickers offer and who is shown: only the active employees can be picked, but the executor and the
   * observers the task already has stay in the lists even when they are suspended now
   */
  private refreshPeople(): void {
    const { assignee_id, observer_ids } = this.form.controls;
    const observerIds = observer_ids.value;
    const known = new Map(this.users.map((user) => [user.id, user]));

    this.assigneeOptions = this.users.filter((user) => user.isActive || user.id === assignee_id.value);
    this.observerOptions = this.users.filter((user) => user.isActive && !observerIds.includes(user.id));
    this.observers = observerIds.map(
      (id) =>
        known.get(id) ?? {
          id,
          name: String(this.translateService.instant('crm_tasks.unknown_user')),
          isActive: false,
        },
    );
  }
}
